import { useCallback, useContext, useEffect, useId, useMemo, useState } from "react";

import { FloatingTree } from "@floating-ui/react";
import { throttle } from "es-toolkit";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { useSuggestionsActions } from "@/components/suggestions/actions/useSuggestionsActions";
import { getNestedList } from "@/components/suggestions/list/utils";
import { SuggestionsListProvider } from "@/components/suggestions/navigation/SuggestionsListProvider";
import { useSuggestionsNavigation } from "@/components/suggestions/navigation/useSuggestionsNavigation";
import { useSuggestions } from "@/components/suggestions/useSuggestions";
import { useMenuSearchable } from "@/components/suggestions/utils/useMenuSearchable";
import { getIsMobile } from "@/utils/getIsMobile";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { SuggestionsGroupsList } from "./list/SuggestionsGroupsList";
import { getSuggestionsDoc } from "./suggestionsDoc";
import { SuggestionsContext } from "./SuggestionsProvider";
import { useAcceptedProviders } from "./utils/useAcceptedProviders";

import type {
  SuggestionProviderType,
  ProviderSearchOptions,
  SelectSuggestionFn,
  SuggestionsGroup,
} from "@/components/suggestions/types";
import type { TiptapSuggestionsRenderPlace } from "@/components/tiptap/tiptap-suggestions/TiptapSuggestions";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FloatingContext } from "@floating-ui/react";

export type Props = Omit<React.ComponentProps<"div">, "onSelect"> & {
  value: string;
  doc: DocumentModel;
  accept?: SuggestionProviderType[];
  providerSearchOptions?: ProviderSearchOptions;
  renderContext?: "math" | "text";
  floatingContext?: FloatingContext;
  initialIndex?: number;
  onSelect: SelectSuggestionFn;
  place?: TiptapSuggestionsRenderPlace;
};

export const Suggestions = observer(function Suggestions({
  ref,
  value,
  doc,
  accept,
  providerSearchOptions,
  renderContext,
  floatingContext,
  onSelect,
  initialIndex,
  className,
  id,
  ...props
}: Props) {
  const generatedMenuId = useId();
  const menuId = id ?? generatedMenuId;
  const { suggestionsRenderPlace } = useContext(SuggestionsContext);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  const trimmedValue = value.trim();
  const isMobile = getIsMobile();
  const suggestionsDoc = getSuggestionsDoc();

  const filteredAccept = useAcceptedProviders(accept, doc);

  const { listGroups, isPending } = useSuggestions(trimmedValue, doc, filteredAccept, {
    providerSearchOptions,
    flat: isMobile || suggestionsRenderPlace !== "tiptap",
    initialIndex,
  });

  const handleSelect = useCallback<SelectSuggestionFn>(
    (suggestion, e, isSelectedManually) => {
      if (!suggestion || suggestion.disabled) {
        return;
      }

      if (suggestion.trackingId) {
        trackEvent("suggestions_action_selected", {
          action: suggestion.trackingId,
          suggestion_type: suggestion.type,
          context: doc.editor.suggestions.openContext,
          doc_id: doc.id,
        });
      }

      onSelect(suggestion, e, isSelectedManually);
    },
    [onSelect, doc],
  );

  const handleOverlayClick = () => {
    doc.editor.resetInput();

    if (renderContext === "text") {
      doc.editor.tiptap?.commands.focus();
    }
  };

  useEffect(() => {
    return () => {
      suggestionsDoc.clear();
    };
  }, [suggestionsDoc]);

  const suggestionsStyle = getSuggestionsStyle(
    suggestionsRenderPlace,
    isMobile,
    floatingContext?.y,
    floatingContext?.floatingStyles,
    isChatExpanded,
  );

  const handleScroll = useMemo(
    () =>
      throttle((e: React.UIEvent<HTMLDivElement>) => {
        if (suggestionsRenderPlace !== "chat" || isChatExpanded) return;
        if (e.currentTarget.scrollTop > 0) setIsChatExpanded(true);
      }, 100),
    [suggestionsRenderPlace, isChatExpanded],
  );

  const isSearchable = useMenuSearchable(doc.editor.suggestions, null);
  const isSearching = isSearchable && doc.editor.suggestions.query.length > 0;
  const isVisible = getIsVisible(doc, listGroups) || isSearching;

  const onActiveIndexChange = useCallback(
    (listId: string | null, index: number, fromKeyboard = false) => {
      runInAction(() => {
        doc.editor.suggestions.nestedListId = listId;
        doc.editor.suggestions.activeIndex = index;
        doc.editor.suggestions.activeIndexFromKeyboard = fromKeyboard;
      });
    },
    [doc.editor.suggestions],
  );

  const { nestedListId } = doc.editor.suggestions;

  const list = useMemo(() => getNestedList(listGroups, nestedListId), [listGroups, nestedListId]);

  useSuggestionsNavigation({
    editor: doc.editor,
    listId: nestedListId,
    list,
    onActiveIndexChange,
    // Gate on the rendered popup, not editor.suggestions.visible: sidebar fields
    // (NodeValueSuggestionsField) show this popup while the editor input is idle.
    disabled: isPending || !isVisible,
  });

  useSuggestionsActions({
    value,
    editor: doc.editor,
    renderContext,
    onSelect: handleSelect,
  });

  return (
    <>
      {isMobile && isVisible && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={twMerge(
            "hidden sm:flex",
            "fixed z-40 top-0 left-0 bottom-0 right-0 w-full h-full",
          )}
          onClick={handleOverlayClick}
        />
      )}
      <FloatingTree>
        <SuggestionsListProvider
          menuId={menuId}
          onSelect={handleSelect}
          onActiveIndexChange={onActiveIndexChange}
        >
          <SuggestionsGroupsList
            ref={ref}
            id={menuId}
            listId={null}
            doc={suggestionsDoc}
            suggestions={doc.editor.suggestions}
            groups={listGroups}
            style={suggestionsStyle}
            aria-busy={isPending}
            aria-hidden={!isVisible}
            data-suggestions-box
            className={twMerge(!isVisible && "hidden", className)}
            onScroll={handleScroll}
            {...props}
          />
        </SuggestionsListProvider>
      </FloatingTree>
    </>
  );
});

function getSuggestionsStyle(
  suggestionsRenderPlace: TiptapSuggestionsRenderPlace,
  isMobile: boolean,
  translateY?: number,
  style?: React.CSSProperties,
  isChatExpanded?: boolean,
): React.CSSProperties {
  if (suggestionsRenderPlace === "tiptap" && isMobile) {
    return {
      ...style,
      width: "calc(100vw - 20px)",
      maxHeight: "180px",
      transform: `translate3d(10px, ${translateY ?? 0}px, 0)`,
    };
  }

  if (suggestionsRenderPlace === "tiptap" && !isMobile) {
    return { ...style };
  }

  if (suggestionsRenderPlace === "chat") {
    return {
      position: "absolute",
      zIndex: 50,
      left: "10px",
      right: "10px",
      minWidth: 0,
      maxWidth: "none",
      maxHeight: isChatExpanded ? "193px" : "108px",
      transition: "max-height 150ms ease-out",
      top: 0,
      transform: "translateY(-100%)",
      borderBottomLeftRadius: "0",
      borderBottomRightRadius: "0",
    };
  }

  return {};
}

function getIsVisible(doc: DocumentModel, listGroups: SuggestionsGroup[]): boolean {
  const onlyStandaloneSuggestions = listGroups.every((group) =>
    group.items.every((s) => s.hideStandalone === true),
  );
  const isInlineMode = doc.editor.suggestions.mode === "inline";

  return listGroups.length > 0 && !onlyStandaloneSuggestions && !isInlineMode;
}
